import React, { FC, Fragment, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Alert, Button } from '@mui/material';
import { ScannerCase, ScannerFileProcessingStatus } from 'models/scanner-case';
import InfoText from 'components/shared/info-text.component';
import { socketIo } from 'context/socket';
import { OrderDetailsContext } from 'pages/order-details/order-details-context';
import { startFileImportBackgroundProcessTemplate } from 'apollo/templates/scanner-case';
import { useMutation } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import RestorationsTable from 'components/scanner-cases/shared/restorations-table/restorations-table.component';
import ScannerCaseDenthubFileTable from 'components/scanner-cases/shared/scanner-case-denthub-file-table/scanner-case-denthub-file-table.component';
import ScannerCasePreview from 'components/scanner-cases/shared/scanner-case-dialog/scanner-case-preview/scanner-case-preview.component';
import { currentOrgVar } from 'apollo/reactive-variables/organization';

type ScannerCaseInfoProps = {
    scannerCase: ScannerCase;
};
const ScannerCaseInfo: FC<ScannerCaseInfoProps> = ({ scannerCase }) => {
    const { t: translate } = useTranslation(['orders', 'scan-cases']);
    const socket = useContext(socketIo.getSocketContext());
    const { refetch } = useContext(OrderDetailsContext);
    const { showBoundary } = useDenthubErrorBoundary();
    const currentOrg = currentOrgVar();

    useEffect(() => {
        const handleFilesProcessComplete = (event: { data: { scannerCaseId: string } }) => {
            if (event.data.scannerCaseId === scannerCase._id) {
                refetch();
            }
        };

        socket.on('SCANNER_CASE_FILES_PROCESS_COMPLETE', handleFilesProcessComplete);
        return () => {
            // Remove only this listener — removeAllListeners would also detach the
            // handlers registered by the surrounding dialog and scanner case table.
            socket.off('SCANNER_CASE_FILES_PROCESS_COMPLETE', handleFilesProcessComplete);
        };
    }, [socket, scannerCase]);

    const [startFileImportBackgroundProcess] = useMutation(startFileImportBackgroundProcessTemplate, {
        onCompleted: ({ startFileImportBackgroundProcess }) => {
            if (startFileImportBackgroundProcess) {
                refetch();
            }
        },

        onError: (error) => {
            showBoundary(error);
        },
    });

    const handleTryAgain = () => {
        startFileImportBackgroundProcess({ variables: { caseId: scannerCase._id } });
    };

    return (
        <Fragment>
            <Box display="flex">
                <Box flexGrow={1} mr={2}>
                    <Box mb={2}>
                        <Typography variant="h4">{translate(`scan-cases:${scannerCase.source}_INFO`)}</Typography>
                    </Box>
                    <Box mb={2}>
                        {translate('scan-cases:SCAN_CASE')}: {scannerCase.displayCaseId}
                    </Box>
                    <Box mb={2}>
                        {scannerCase.comments ? (
                            <Box>{scannerCase.comments}</Box>
                        ) : (
                            <InfoText text={translate('orders:NO_COMMENTS_AVAILABLE')} />
                        )}
                    </Box>
                </Box>
                <Box flexGrow={0} sx={{ minWidth: 150 }} alignSelf="flex-start">
                    <ScannerCasePreview scannerCase={scannerCase} height={100} />
                </Box>
            </Box>
            <Box mb={2}>
                <RestorationsTable restorations={scannerCase.restorations} />
            </Box>
            <Box>
                {scannerCase.fileProcessingStatus === ScannerFileProcessingStatus.PROCESSING ? (
                    <Alert severity="info">{translate('scan-cases:SCANNER_FILES_STILL_PROCESSING')}</Alert>
                ) : (scannerCase.fileProcessingStatus === ScannerFileProcessingStatus.ERROR ||
                      scannerCase.fileProcessingStatus === ScannerFileProcessingStatus.NOT_STARTED) &&
                  currentOrg?._id.toString() === scannerCase?.organization?._id.toString() ? (
                    <Alert
                        severity="error"
                        action={
                            <Button size="small" onClick={handleTryAgain}>
                                {translate('common:TRY_AGAIN')}
                            </Button>
                        }
                    >
                        {translate('scan-cases:SCANNER_FILES_PROCESSING_ERROR')}
                    </Alert>
                ) : (
                    <ScannerCaseDenthubFileTable scannerCase={scannerCase} />
                )}
            </Box>
        </Fragment>
    );
};

export default ScannerCaseInfo;
